import { useState } from 'react';
import { Accordion, tokens, Text, Icon, Link, Stack } from '@hotjar/hotsauce';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { SiteCostChanges } from '../cost-changes-breakdown/SiteCostChanges';
import type {
  BillingPeriod,
  ProductAllowance,
} from '~/subscription-management/api/common/model/subscription';
import type { OrganizationListItem } from '~/subscription-management/features/service-resource-list/hooks/useServiceResourceList';
import type { Currency } from '~/subscription-management/api/common/model/customer';
import { sitesHaveActiveChanges } from '../utils';

const S = {
  Accordion: styled(Accordion)`
    background-color: ${tokens.ALIAS_COLORS.ui_background};
  `,
  AccordionItem: styled(Accordion.Section)`
    padding: ${tokens.spacer.x2};
  `,
  AccordionHeader: css`
    background-color: ${tokens.ALIAS_COLORS.bg_info};
    padding: ${tokens.spacer.x3} ${tokens.spacer.x2};
  `,
  AccordionTitle: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  SitesContainer: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacer.x2};
  `,
  ChurningMessage: styled(Text)`
    display: flex;
    gap: ${tokens.spacer.x1};
    align-items: center;
  `,
  SiteCostChanges: styled(SiteCostChanges)`
    &:not(:last-child) {
      border-bottom: 1px solid ${tokens.ALIAS_COLORS.ui_border};
      padding-bottom: ${tokens.spacer.x2};
    }
  `,
};

interface Props {
  defaultExpanded?: boolean;
  allResources?: OrganizationListItem[];
  updatedProducts: ProductAllowance[];
  billingPeriod: BillingPeriod;
  currency: Currency;
  isChurning?: boolean;
  showCosts?: boolean;
  title?: string;
  edit?: () => void;
  loading?: boolean;
}

export function CostChangesSummary({
  defaultExpanded = false,
  allResources,
  updatedProducts,
  billingPeriod,
  currency,
  isChurning = false,
  showCosts = true,
  title,
  edit,
  loading = false,
}: Props): JSX.Element {
  const { t: tSummary } = useTranslationWithPrefix('checkout:wizard.cost-changes.summary');
  const { t: tChurn } = useTranslationWithPrefix('checkout:churn');
  const [activeSection, setActiveSection] = useState<number>();
  const defaultActiveSection = defaultExpanded ? 0 : -1;

  return (
    <S.Accordion
      size="s"
      setUncontrolled
      sectionToShow={activeSection ?? defaultActiveSection}
      onChange={setActiveSection}
    >
      <S.AccordionItem
        title={
          <S.AccordionTitle>
            <Text>{title ?? tSummary('title')}</Text>

            {isChurning && (
              <Stack>
                <S.ChurningMessage textColor="text_danger">
                  <Icon icon="warning" />
                  {tChurn('warning-message')}
                </S.ChurningMessage>
                <Link kind="standalone" as="button" onClick={edit}>
                  {tSummary('edit')}
                </Link>
              </Stack>
            )}
          </S.AccordionTitle>
        }
        headerCustomCss={S.AccordionHeader}
      >
        <S.SitesContainer>
          {allResources?.map((org) =>
            org.sites.map((site) => {
              if (sitesHaveActiveChanges([site], updatedProducts)) {
                return (
                  <S.SiteCostChanges
                    key={site.id}
                    site={site}
                    updatedProducts={updatedProducts}
                    billingPeriod={billingPeriod}
                    currency={currency}
                    showCosts={showCosts}
                    bundleDiscount={site.bundleDiscount}
                    legacyDiscount={site.legacyDiscount}
                    loading={loading}
                  />
                );
              }

              return null;
            }),
          )}
        </S.SitesContainer>
      </S.AccordionItem>
    </S.Accordion>
  );
}
